import {
  useCustomer,
  useCustomerLogout,
  useSettings,
} from '@backpackjs/storefront';
import { useRouter } from 'next/router';
import { useMemo, useState, useEffect } from 'react';

import { useMultipass } from '../hooks/useMultipass';
import { useDataLayerActions } from '../hooks';
import { Body, Button, Link, Subheading, Svg } from '../snippets';

function AccountLayout({ children }) {
  const settings = useSettings();
  const customer = useCustomer();
  const { asPath, query } = useRouter();
  const { customerLogout } = useCustomerLogout();
  const multipass = useMultipass();
  const { sendLogInEvent } = useDataLayerActions();

  // send login event on load
  useEffect(() => {
    if (!query?.login || !query.login === 'success') {
      return;
    }

    sendLogInEvent();

    // replace url to remove query param without reloading page
    window.history.replaceState({}, document.title, window.location.pathname);
  }, [query]);

  const isSubscriber = customer?.tags?.filter((str) => {
    return (
      (typeof str === 'string' && str.indexOf('Active Subscriber') === 0) ||
      (typeof str === 'string' && str.indexOf('Inactive Subscriber') === 0)
    );
  });

  const {
    menuItems: accountMenuItems,
    helpHeading,
    helpEmail,
    helpPhone,
    callHours,
    chatHours,
  } = {
    ...settings?.account?.menu,
  };

  const menuItems = accountMenuItems
    ?.filter((item) => {
      if (
        isSubscriber?.length === 0 &&
        item?.link?.url ===
          'https://www.clearlyfiltered.com/tools/recurring/login'
      ) {
        return;
      }
      return item;
    })
    .map((item) => {
      const { link } = item;
      return link;
    });

  const [menuOpen, setMenuOpen] = useState(false);

  const activeMenuItem = useMemo(() => {
    return menuItems?.find((link) => {
      return asPath.startsWith(link?.url);
    });
  }, [asPath, menuItems]);

  const handleMenuItemClick = async (e, url) => {
    if (url.includes('/tools/recurring/')) {
      e.preventDefault();

      const res = await multipass.generateUrl({
        customer: { email: customer?.email },
        returnTo: 'https://shop.clearlyfiltered.com/tools/recurring/login',
      });

      if (res?.url) {
        window.location.href = res.url;
      }
    }
  };

  const openChat = () => {
    if (typeof window.GorgiasChat !== 'undefined') {
      window.GorgiasChat.open();
      return;
    }

    window.open(
      'https://help.clearlyfiltered.com',
      '_blank',
      'noopener noreferrer'
    );
  };

  return (
    <div className="px-contained py-contained">
      <div className="mx-auto grid w-full max-w-[80rem] grid-cols-1 gap-8 md:grid-cols-[12rem_1fr] lg:grid-cols-[16rem_1fr] lg:gap-32">
        <div>
          <div className="flex flex-row justify-between gap-6 pb-6 md:flex-col md:justify-start">
            <div className="mb-4 flex-1 text-left md:text-right">
              <Body variant="xs" className="text-shadowGray">
                Home / My Account / {activeMenuItem?.text}
              </Body>
            </div>

            <nav className="hidden gap-6 text-right md:grid">
              <Subheading variant="medium" className="tracking-[-.01em]">
                My account
              </Subheading>
              {menuItems && (
                <ul className="mb-2">
                  {menuItems.map((item) => (
                    <li key={item.url} className="mb-2">
                      <Link
                        className="hover-text-underline"
                        href={item.url}
                        onClick={(e) => handleMenuItemClick(e, item.url)}
                      >
                        {activeMenuItem?.text === item.text ? (
                          <strong>{item.text}</strong>
                        ) : (
                          <span className="font-light">{item.text}</span>
                        )}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <span
                      tabIndex="0"
                      role="button"
                      onKeyDown={(e) =>
                        e.key === 'enter' ? customerLogout() : null
                      }
                      className="hover-text-underline font-light"
                      onClick={customerLogout}
                    >
                      Log Out
                    </span>
                  </li>
                </ul>
              )}

              {helpHeading && (
                <Subheading variant="medium">{helpHeading}</Subheading>
              )}
              <ul className="grid gap-4">
                <li className="mb-4">
                  <Body>Email us</Body>
                  <Link className="text-underline" href={`mailto:${helpEmail}`}>
                    {helpEmail}
                  </Link>
                </li>
                <li className="mb-4">
                  <Body>Call us</Body>
                  <Link className="text-underline" href={`tel:${helpPhone}`}>
                    {helpPhone}
                  </Link>
                  <Body>{callHours}</Body>
                </li>
                <li>
                  <Body>Chat with us</Body>
                  <Button
                    theme="secondary"
                    variant="filled"
                    onClick={openChat}
                    className="!my-1 max-w-[210px] items-center !py-2"
                  >
                    Online Now!
                  </Button>
                  <Body>{chatHours}</Body>
                </li>
              </ul>
            </nav>
          </div>

          <div className="relative w-full md:hidden">
            <button
              aria-label="Open account menu"
              className="flex h-14 w-full items-center justify-between gap-4 rounded-full border border-[#DDE2E7] bg-white px-5 text-base"
              onClick={() => setMenuOpen(!menuOpen)}
              type="button"
            >
              <p>{activeMenuItem?.text}</p>

              <Svg
                className={`w-4 text-text ${menuOpen ? 'rotate-180' : ''}`}
                src="/svgs/chevron-down.svg#chevron-down"
                title="Chevron Down"
                viewBox="0 0 24 24"
              />
            </button>

            {menuItems && (
              <nav
                className={`absolute left-0 top-[calc(100%+0.5rem)] z-10 w-full rounded-[0.5rem] border border-[#DDE2E7] bg-white text-base transition duration-100 ${
                  menuOpen
                    ? 'pointer-events-auto translate-y-0 opacity-100'
                    : 'pointer-events-none -translate-y-1 opacity-0'
                }`}
              >
                <ul className="flex max-h-[18rem] flex-col gap-0 overflow-y-auto py-2 scrollbar-hide">
                  {menuItems.map((link, index) => {
                    const isActive = link?.url === activeMenuItem?.url;

                    return link?.text ? (
                      <li key={index} className="flex">
                        <Link
                          aria-hidden={menuOpen ? 'true' : 'false'}
                          aria-label={link.text}
                          aria-selected={isActive ? 'true' : 'false'}
                          className={`w-full px-5 py-1.5 transition md:hover:bg-offWhite ${
                            isActive ? 'bg-lightGray' : 'bg-transparent'
                          }`}
                          href={link.url}
                          newTab={link.newTab}
                          tabIndex={menuOpen ? '0' : '-1'}
                          onClick={(e) => handleMenuItemClick(e, link.url)}
                        >
                          {link.text}
                        </Link>
                      </li>
                    ) : null;
                  })}
                </ul>
              </nav>
            )}
          </div>
        </div>

        {children}

        <div className="text-center md:hidden">
          <ul className="grid gap-4">
            <li className="border-t border-t-[#2026351A] py-[25px]">
              <Svg
                className="mx-auto mb-2 w-[25px] text-textLink"
                src="/svgs/phone.svg#phone"
                title="Call US"
                viewBox="0 0 25 25"
              />
              <Body className="mb-2 text-[18px]">Call us</Body>
              <Link className="font-light" href={`tel:${helpPhone}`}>
                {helpPhone}
              </Link>
              <Body>{callHours}</Body>
            </li>
            <li className="border-t border-t-[#2026351A] py-[25px]">
              <Svg
                className="mx-auto mb-2 w-[26px] text-textLink"
                src="/svgs/help.svg#help"
                title="Email Us"
                viewBox="0 0 26 26"
              />
              <Body className="mb-2 text-[18px]">Email us</Body>
              <Link
                className="text-underline font-light"
                href={`mailto:${helpEmail}`}
              >
                {helpEmail}
              </Link>
            </li>
            <li className="mb-[40px] border-b border-t border-b-[#2026351A] border-t-[#2026351A] py-[25px]">
              <Svg
                className="mx-auto mb-2 w-[26px] text-textLink"
                src="/svgs/headset.svg#headset"
                title="Call US"
                viewBox="0 0 26 28"
              />

              <Body className="mb-2 text-[18px]">Chat with us</Body>
              <Button
                theme="secondary"
                variant="filled"
                className="!my-1 max-w-[210px] items-center !py-3 !text-[17px]"
                onClick={openChat}
              >
                Online Now!
              </Button>
              <Body>{chatHours}</Body>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

AccountLayout.displayName = 'AccountLayout';
export default AccountLayout;
